import { BIOMES, setBiomeMap } from '@thicket/core';
import { labArt, loadLab } from '../garden/art/lab/index.js';
import { update } from './app.js';

/**
 * When the garden art (the Thicket RPG Lab page) loads. Starting the Lab keeps the page
 * busy for a while, so the app paints the task lists first and starts it once the page is
 * idle, or straight away when something can't show without it (the garden, the starting
 * place chooser). Until then the tabs show their glyphs and the garden says it's growing.
 */
export type ArtState = 'waiting' | 'loading' | 'ready' | 'unavailable';

let state: ArtState = 'waiting';
let pending: Promise<void> | null = null;

export const artState = () => state;

/**
 * Loads the Lab and registers its ground as the planting maps, so tapping open grass and
 * "plant everything" agree with what's drawn. The server ships the same maps baked into
 * core, so a plant placed here is accepted there. Calling it again while it loads is free;
 * after it couldn't load, calling it tries again.
 */
export function startLab(): Promise<void> {
  if (state === 'ready') return Promise.resolve();
  if (pending) return pending;
  state = 'loading';
  pending = loadLab().then(
    () => {
      for (const b of BIOMES) setBiomeMap(b, labArt.map(b));
      state = 'ready';
      pending = null;
      update();
    },
    (err: unknown) => {
      console.warn('The garden art could not load', err);
      state = 'unavailable';
      pending = null;
      update();
      throw err;
    },
  );
  return pending;
}

/** Starts the Lab once the current screen has painted and the page has a quiet moment. */
export function startLabWhenIdle() {
  if (state !== 'waiting') return;
  const go = () => void startLab().catch(() => {});
  const idle = (window as { requestIdleCallback?: (fn: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
  // Two frames: the first paints what's on screen, the second starts after that paint.
  requestAnimationFrame(() => requestAnimationFrame(() => (idle ? idle(go, { timeout: 1500 }) : setTimeout(go, 200))));
}

/** Starts the Lab now, for screens that need it (it's a no-op once started). */
export function needLab() {
  if (state === 'waiting') queueMicrotask(() => void startLab().catch(() => {}));
}

// Back online after the art couldn't load: try again by itself.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    if (state === 'unavailable') void startLab().catch(() => {});
  });
}
